// Ensure the Primary package exists for a product scope (same shape as alternates).

import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  DEFAULT_PROPOSAL_ALTERNATE_KEY,
  DEFAULT_PROPOSAL_ALTERNATE_PAGES,
  defaultAlternateForArchetype,
  type ProposalAlternate,
  type ProposalAlternatePage,
  type ProposalBuiltinPageKind,
} from "./proposalAlternateTypes";
import {
  listProposalAlternates,
  salesAlternateChoices,
  upsertProposalAlternate,
} from "./proposalAlternateClient";
import { listProposalPages } from "./proposalPageClient";
import { PRIMARY_PAGE_KEYS, type ProposalPage } from "./proposalPageTypes";
import type { OpportunityDocumentArchetype } from "./documentArchetypes";

export const PRIMARY_PROPOSAL_PACKAGE_LABEL = "Primary";

export function isPrimaryProposalPackageKey(key: string): boolean {
  return key.trim().toLowerCase() === DEFAULT_PROPOSAL_ALTERNATE_KEY;
}

/** Bind unset builtin playlist slots to primary library page ids. */
export function withPrimaryPageIds(
  pages: ProposalAlternatePage[],
  library: ProposalPage[],
): ProposalAlternatePage[] {
  const primaryByArchetype = new Map(
    library
      .filter((p) => p.page_key === PRIMARY_PAGE_KEYS[p.archetype])
      .map((p) => [p.archetype, p.id] as const),
  );
  return pages.map((p) => {
    if (p.page_kind === "doc_ref") return p;
    if (p.proposal_page_id) return p;
    const id = primaryByArchetype.get(p.page_kind as ProposalBuiltinPageKind);
    return id ? { ...p, proposal_page_id: id } : p;
  });
}

export function findPrimaryProposalPackage(
  alternates: readonly ProposalAlternate[],
): ProposalAlternate | undefined {
  return alternates.find((a) => isPrimaryProposalPackageKey(a.alternate_key));
}

/**
 * Select options for sales/manager package pickers: real Primary when present,
 * otherwise the synthetic Primary fallback, then other complete packages.
 */
export function proposalPackageSelectOptions(args: {
  alternates: readonly ProposalAlternate[];
  formatKey: OpportunityFormatKey;
  productType: string;
  archetype?: OpportunityDocumentArchetype;
}): ProposalAlternate[] {
  const archetype = args.archetype ?? "proposal";
  const choices = salesAlternateChoices(
    args.alternates.filter((a) => (a.archetype ?? "proposal") === archetype),
  );
  if (findPrimaryProposalPackage(choices)) return choices;
  return [
    defaultAlternateForArchetype({
      formatKey: args.formatKey,
      productType: args.productType,
      archetype,
    }),
    ...choices,
  ];
}

/**
 * Create the Primary package once for a scope (no-op if it already exists).
 * Wired from first page save and from Packages when the list is empty.
 */
export async function ensurePrimaryProposalPackage(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}): Promise<
  | { ok: true; created: boolean; alternate: ProposalAlternate | null }
  | { ok: false; error: string }
> {
  const { formatKey, productType } = args;
  const [altRes, pageRes] = await Promise.all([
    listProposalAlternates({
      format_key: formatKey,
      product_type: productType,
      archetype: "proposal",
    }),
    listProposalPages({
      format_key: formatKey,
      product_type: productType,
      ensure: true,
    }),
  ]);
  if (!altRes.ok) return { ok: false, error: altRes.error };

  const existing = findPrimaryProposalPackage(
    altRes.data.alternates.filter((a) => (a.archetype ?? "proposal") === "proposal"),
  );
  if (existing) return { ok: true, created: false, alternate: existing };

  const library =
    pageRes.ok && pageRes.data.pages_supported !== false ? pageRes.data.pages : [];
  const pages = withPrimaryPageIds(
    DEFAULT_PROPOSAL_ALTERNATE_PAGES.map((p) => ({ ...p })),
    library,
  );

  const upserted = await upsertProposalAlternate({
    format_key: formatKey,
    product_type: productType,
    alternate_key: DEFAULT_PROPOSAL_ALTERNATE_KEY,
    label: PRIMARY_PROPOSAL_PACKAGE_LABEL,
    sort_order: 0,
    is_default: true,
    archetype: "proposal",
    pages,
  });
  if (!upserted.ok) return { ok: false, error: upserted.error };
  return { ok: true, created: true, alternate: upserted.data.alternate };
}
